import React, { useEffect } from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Privacy Policy</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
            {/* INSERT PRIVACY POLICY CONTENT HERE */}
            <div className="bg-white rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 text-center mb-6 sm:mb-8">
                CADENCE IO LLC PRIVACY POLICY
              </h1>
              
              <div className="mb-6 p-4 sm:p-6 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm sm:text-base text-blue-800 leading-relaxed font-medium">
                  <strong>NOTE:</strong> The Cadence IO app, described further herein and in our SaaS Agreement and Terms of Service, is in beta phase. As such, users should understand occasional changes and/or improvements may be made to the app.
                </p>
              </div>

              <p className="mb-6 text-sm sm:text-base text-gray-700 leading-relaxed">
                Cadence IO LLC, doing business as Cadence IO ("we," "our," "us," or "Cadence IO"), prepared this Privacy Policy to help you understand our practices with respect to the collection, use, and disclosure of information we collect from you through: (i) cadenceio.com, its subdomains, and any other website where our Terms of Service are posted; (ii) our online hosted services; and (iii) our "Software," meaning, collectively, our B2B SaaS platform enabling record labels, artist managers, and distributors to submit, distribute, and manage marketing information across Digital Service Providers (DSPs), and associated tools and documentation ((i) through (iii) collectively, are the "Services").
              </p>

              <p className="mb-6 text-sm sm:text-base text-gray-700 leading-relaxed">
                The user or client ("you," "your," "Client," or "User") is the person who intends to utilize the Cadence IO System and related Services, and is bound to the terms herein upon acceptance and agreement to use the Cadence IO System and Services, as referenced in the Beta SaaS Agreement and/or Terms of Service.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                1. What Information We Collect and How
              </h2>
              
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We collect information from you when you:
              </p>
              <ul className="list-disc ml-6 mb-6 text-sm sm:text-base text-gray-700 leading-relaxed space-y-1">
                <li>Create or register an account, or administer your account</li>
                <li>Input, post, or upload information, data, or other content through the Services</li>
                <li>Submit questions, requests, or other communications to us via various communication channels</li>
                <li>Contact us for customer support or technical support</li>
                <li>Visit any of our websites or download any of our applications</li>
                <li>Participate in promotions, user conferences, webinars, demonstrations, contests, sweepstakes, surveys, or other marketing events</li>
                <li>Participate in research activities</li>
                <li>Interact with other users of the Services and guests on our community forums</li>
                <li>Integrate third-party products and services with your account on our Services ("Cadence IO Account")</li>
              </ul>

              <p className="mb-6 text-sm sm:text-base text-gray-700 leading-relaxed">
                We may also obtain publicly available business contact information.
              </p>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                a. Your Content
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                "Content" includes all marketing materials, pitch data, and assets used for submission of pitches to DSPs by or through the Services. It also includes information you provide when connecting a third-party account, product, or service to the Services, including DSP login credentials. You (and anyone who can interact with your use of the Services) own and control the nature of any Content, subject to any applicable terms and conditions including our Terms of Service and this Privacy Policy.
              </p>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                b. Customer Information
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                "Customer Information" consists of information related to your access or use of our Services, the creation of accounts, or that otherwise identifies you as a customer or end user of the Services. Customer Information includes:
              </p>
              <ul className="list-disc ml-6 mb-4 text-sm sm:text-base text-gray-700 leading-relaxed space-y-1">
                <li><strong>Identifiers:</strong> This includes your name, account username, email address, and other similar identifiers.</li>
                <li><strong>Customer Records:</strong> This includes username and password, payment information, company name, job title, business email address, department, usage logs, pitch submission content, and linked third-party DSP accounts (if OAuth involved).</li>
                <li><strong>Commercial Information:</strong> This includes information about products or services purchased, obtained or considered.</li>
                <li><strong>Internet/Network Activity Information:</strong> This includes your browsing history, log and analytics data, information about the device(s) used to access the Services, domain server, search history and information regarding your interaction with our Services, and other usage data.</li>
                <li><strong>Audio/Visual Information:</strong> This includes pictures you provide or upload in connection with our Services, and the content and audio or video recordings of phone or video calls between you and us that we record where permitted by law.</li>
                <li><strong>Profession/Employment Information:</strong> This includes your current employer, title, and location.</li>
                <li><strong>Other Personal Information:</strong> This includes personal information you provide to us in relation to a survey, comment, question, request, or inquiry, and information you provide when you connect a third-party account, product or service to the Services.</li>
              </ul>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                c. Third-Party Information
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                Certain aspects of the Services allow you to link or integrate third-party products and services to enable certain features and functionalities with the Services. Specifically, Cadence IO may connect with DSPs (i.e. Deezer, Spotify, Apple Music, and others) via OAuth or other authorized methods. If you choose to use these features or functionalities, you may be asked to create an account with a third party that provides such features or functionalities or link your existing third-party service account with the Services.
              </p>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                d. Automatically-Collected Information
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We may collect information about how you use the Services and your actions on the Services, including your IP addresses, operating systems, ISPs, platform types, and device types. We may also use tools, including third-party tools, to collect analytics data. Some of this information is collected through the use of cookies and other tracking technologies.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                2. How We Use the Information We Collect
              </h2>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                a. Use of Content
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                Notwithstanding anything to the contrary in this Privacy Policy, we will not use or access your Content except: to provide, maintain, improve, or optimize use of the Services; where you (or the organization through which you have an account) explicitly approve access; in response to lawful requests by law enforcement or government authorities; where necessary to ensure the stability and security of the Services and our systems; and where necessary to protect the rights, privacy, safety, or property of you, us, or others.
              </p>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                b. Use of Other Information
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We use the information we collect for a variety of purposes, including:
              </p>
              <ul className="list-disc ml-6 mb-4 text-sm sm:text-base text-gray-700 leading-relaxed space-y-1">
                <li><strong>Fulfilling our Contract with You and Providing the Services to You</strong></li>
                <li><strong>Analyzing and Improving our Services Pursuant to our Legitimate Interest</strong></li>
                <li><strong>Marketing Our Products and Services</strong></li>
                <li><strong>Legal Obligations and Rights</strong></li>
                <li><strong>Participation in any Cadence IO partner program</strong></li>
                <li><strong>Consent</strong></li>
                <li><strong>Aggregated or De-Identified Data</strong></li>
                <li><strong>Other Purposes</strong></li>
              </ul>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                3. How We Disclose Information
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We disclose information we collect for specific purposes, including:
              </p>
              <ul className="list-disc ml-6 mb-4 text-sm sm:text-base text-gray-700 leading-relaxed space-y-1">
                <li>When you ask us to, or otherwise give your specific consent</li>
                <li>With affiliates and subsidiaries to provide you with aspects of the Services</li>
                <li>With vendors that help us provide you with aspects of the Services</li>
                <li>With third parties who enable certain features or functionalities of the Services</li>
                <li>As necessary to comply with applicable law</li>
                <li>As necessary in the event of a proposed or actual reorganization, merger, sale, or other disposition</li>
              </ul>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                4. Transfers of Information
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We may transfer to and process your personal information in countries outside of the jurisdiction where you are located for the various purposes described above. When required by law, we will ensure that we rely on an appropriate legal mechanism for the transfer.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                5. Retention of Information
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We will delete login credentials, DSP login credentials, and related personal information within thirty (30) days of the date of your termination of your account. However, we will maintain the data you submit to us via our form for pitch purposes, including but not limited to data such as song title, artist name, genres, mood, etc., unless and until you specifically ask us to remove these items from our database.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                6. Managing Your Information
              </h2>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                a. Content
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                You may access, correct, amend, or delete Content within the Services. You own all Content you upload provided you have lawful title thereto. For instructions on how to permanently delete Content from your Cadence IO Account, please contact us at <a href="mailto:admin@cadenceio.com" className="text-blue-600 hover:text-blue-800 underline">admin@cadenceio.com</a>.
              </p>

              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mt-6 mb-3">
                b. Other Information
              </h3>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                We may use some of the information we collect for marketing purposes. If you wish to stop receiving promotional communications or to opt out of use of your information for these purposes, please follow the opt-out instructions by clicking "Unsubscribe" in those communications. You may also contact us at <a href="mailto:admin@cadenceio.com" className="text-blue-600 hover:text-blue-800 underline">admin@cadenceio.com</a> to opt out.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                7. Information from Children
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                Our Services are not intended for use by children under the age of 18 and we do not knowingly collect personal information from children. If we learn that we have collected personal information from a child, we will take reasonable steps to delete such information from our files. If you learn that your child has provided us with personal information without your consent, you may contact us at <a href="mailto:admin@cadenceio.com" className="text-blue-600 hover:text-blue-800 underline">admin@cadenceio.com</a>.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                8. Changes to Privacy Policy
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                Any information that we collect is subject to the Privacy Policy in effect at the time such information is collected. We may, however, revise the Privacy Policy from time to time. If a revision is material, as determined solely by us, we will notify you via email. The current version will always be posted to our Privacy Policy page.
              </p>

              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mt-8 mb-4">
                9. Contact Us
              </h2>
              <p className="mb-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                If you have any questions about our privacy practices, including this policy, you may contact us by email at <a href="mailto:admin@cadenceio.com" className="text-blue-600 hover:text-blue-800 underline">admin@cadenceio.com</a> or by mail at Cadence IO, 4485 Songbird Lane, Evergreen, CO 80439.
              </p>

              {/* Jurisdiction-Specific Sections */}
              <div className="mt-8 p-4 sm:p-6 bg-gray-50 rounded-lg">
                <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-4">
                  Additional Privacy Rights by Jurisdiction
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2">
                      California Residents
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      California residents may have additional rights under the California Consumer Privacy Act (CCPA), including rights to know, correct, delete, and opt-out of personal information sales or sharing.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2">
                      EEA, UK, Swiss Residents
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      Residents of the European Economic Area, United Kingdom, and Switzerland have rights under GDPR, including access, portability, rectification, erasure, restriction, and objection rights.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2">
                      Other US State Residents
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      Residents of Colorado, Connecticut, Delaware, Iowa, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Tennessee, Texas, Utah, and Virginia may have additional privacy rights under applicable state laws.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2">
                      Canadian Residents
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      Canadian residents have rights to access and correct personal information we hold about them.
                    </p>
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-600">
                  To exercise any of these rights, please contact us at <a href="mailto:admin@cadenceio.com" className="text-blue-600 hover:text-blue-800 underline">admin@cadenceio.com</a> with the appropriate subject line for your jurisdiction.
                </p>
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-right rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
